import { Link } from 'react-router-dom';

import Pill from '../shared/Pill';
import { Scroll } from '../shared/Scroll';
import { reasonLabel } from '../shared/labels';
import { dueLabel, isOverdue, resultNote, shiftsLabel } from './callTaskFormat';

import Button from '@/components/Button';
import { Small } from '@/components/Typography';
import { CallTask } from '@/services/lifecycleAPI';
import styled from '@/styles';

// A row is a flex line: the data columns in a grid that scrolls, and the
// action pinned to the right edge of the screen (sticky inside the row, which
// a grid cell cannot be: its containing block is its own grid area). On a
// 1280 px laptop the columns are wider than the screen (bug hunt 13 BH-1301),
// and Log call must stay in reach (Jira 61228, option A).
const Row = styled('div', {
  display: 'flex',
  alignItems: 'stretch',
  minWidth: '1500px',
  borderTop: '1px solid #EDEDED',
  backgroundColor: '#FFFFFF',
  fontSize: '14px',
  variants: {
    head: {
      true: {
        borderTop: 'none',
        backgroundColor: '#FAFAFA',
        fontSize: '12px',
        fontWeight: 600,
        color: '#5A5A5A',
        textTransform: 'uppercase',
        letterSpacing: '.03em',
      },
    },
    clickable: {
      true: { cursor: 'pointer', '&:hover': { backgroundColor: '#FAFAFA' } },
    },
  },
});

const Cells = styled('div', {
  display: 'grid',
  gridTemplateColumns:
    '150px minmax(170px, 1.2fr) 130px 150px minmax(200px, 1.4fr) 120px 140px 190px',
  gap: '12px',
  alignItems: 'center',
  padding: '12px 16px',
  flex: '1 1 auto',
  minWidth: 0,
});

// Inherits the row's background so a hovered row stays one colour; the
// shadow shows there are columns under it.
const Action = styled('div', {
  position: 'sticky',
  right: 0,
  flex: '0 0 auto',
  width: '126px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '12px 16px 12px 8px',
  backgroundColor: 'inherit',
  boxShadow: '-8px 0 8px -8px rgba(0, 0, 0, 0.18)',
});

const Stacked = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  alignItems: 'flex-start',
});

const STATUS: Record<
  CallTask['status'],
  { label: string; tone: 'green' | 'grey' }
> = {
  open: { label: 'Open', tone: 'green' },
  completed: { label: 'Completed', tone: 'grey' },
  skipped: { label: 'Closed', tone: 'grey' },
};

type Props = {
  tasks: CallTask[];
  onOpen: (id: number) => void;
};

const CallTaskTable = ({ tasks, onOpen }: Props) => (
  <Scroll>
    <Row head>
      <Cells>
        <span>Due</span>
        <span>Worker</span>
        <span>City</span>
        <span>Phone</span>
        <span>Journey and step</span>
        <span>Open shifts</span>
        <span>Assigned to</span>
        <span>Status</span>
      </Cells>
      <Action aria-hidden="true" />
    </Row>
    {tasks.map((task) => {
      const status = STATUS[task.status];
      const note = resultNote(task);
      return (
        <Row
          key={task.id}
          clickable
          data-testid={`callTaskRow-${task.id}`}
          onClick={() => onOpen(task.id)}
        >
          <Cells>
            <span
              style={
                isOverdue(task)
                  ? { color: '#B0030A', fontWeight: 700 }
                  : undefined
              }
            >
              {dueLabel(task)}
            </span>
            <Link
              to={`/workers_v2/${task.worker.id}`}
              style={{ fontWeight: 600 }}
              onClick={(e) => e.stopPropagation()}
            >
              {task.worker.name}
            </Link>
            <span>{task.worker.city ?? '-'}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {task.worker.phone_number ?? 'No phone number'}
            </span>
            <Stacked css={{ gap: 0 }}>
              <strong style={{ fontWeight: 600 }}>
                {task.journey_name
                  ? `${task.journey_key} · ${task.journey_name}`
                  : task.journey_key}
              </strong>
              <Small color="light">{task.step_label}</Small>
            </Stacked>
            <span>{shiftsLabel(task.worker.open_shifts_nearby)}</span>
            <span>{task.assigned_to_name ?? 'Nobody'}</span>
            <Stacked>
              <Pill tone={status.tone}>{status.label}</Pill>
              {task.do_not_call_reason && (
                <Pill tone="red">
                  {`Do not call · ${reasonLabel(task.do_not_call_reason).label}`}
                </Pill>
              )}
              {note && <Small color="light">{note}</Small>}
            </Stacked>
          </Cells>
          <Action data-testid={`callTaskAction-${task.id}`}>
            <Button
              a11yLabel={`${task.status === 'open' ? 'Log call' : 'View'} ${task.worker.name}`}
              appearance="outline"
              size="sm"
              label={task.status === 'open' ? 'Log call' : 'View'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onOpen(task.id);
              }}
            />
          </Action>
        </Row>
      );
    })}
  </Scroll>
);

export default CallTaskTable;
